/**
 * Helpers untuk filter karakter di komponen number family
 * (NumberInput, CounterInput, PercentInput).
 *
 * Tujuan: cegah user input karakter selain angka, koma (jika diizinkan),
 * dan minus (hanya di awal, hanya satu). Karakter titik (`.`) selalu
 * dicegah — separator ribuan ditambahkan otomatis oleh formatter.
 *
 * Jalur masuk yang dijaga:
 * - **Keyboard typing** → {@link filterNumberInputKey} dipanggil di onKeyDown.
 * - **Paste dari clipboard** → {@link handleNumberInputPaste} dipanggil di onPaste.
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */

import type { KeyboardEvent, ClipboardEvent } from 'react';

/**
 * Konfigurasi filter karakter.
 */
export interface KeyFilterOptions {
  /**
   * Apakah karakter `,` (koma desimal) diizinkan.
   * - `true` untuk decimal mode (MUA, Kurs) & PercentInput.
   * - `false` untuk integer mode (IDR, Quantity, Counter).
   */
  allowComma: boolean;
}

/**
 * Set native `input.value` lalu dispatch event input agar React/Antd tahu.
 *
 * React tracking `<input>` value via prototype getter; set langsung
 * `input.value = X` tidak akan memicu React onChange. Workaround: panggil
 * native setter dari prototype, lalu dispatch synthetic input event.
 *
 * Diekspor karena dipakai juga oleh handler kustom di komponen
 * (mis. comma swap / drop-decimals di NumberInput).
 */
export function setNativeInputValue(input: HTMLInputElement, value: string): void {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  if (nativeSetter) {
    nativeSetter.call(input, value);
  } else {
    input.value = value;
  }
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Filter satu keystroke. Return `true` kalau event sudah di-`preventDefault`
 * (caller harus return early). Return `false` kalau key boleh lewat (caller
 * lanjut ke handler berikutnya / propagate ke onKeyDown user).
 *
 * Karakter diizinkan:
 * - Digits `0`-`9` (selalu)
 * - `,` koma — hanya jika `allowComma=true`
 * - `-` minus — hanya di posisi 0, hanya satu di field
 *
 * Karakter dicegah:
 * - `.` titik (separator ribuan ditambahkan formatter, bukan user)
 * - Huruf, simbol lain (a-z, A-Z, !@#$ dll.)
 *
 * Yang TIDAK difilter (di-pass-through):
 * - Modifier combo (Ctrl/Cmd/Alt + key) → biar bisa Ctrl+A, Ctrl+C, Ctrl+V, dll.
 * - Control key multi-char (Backspace, Delete, Tab, Arrow*, Home, End, Enter, Escape, F-keys)
 */
export function filterNumberInputKey(
  e: KeyboardEvent<HTMLInputElement>,
  options: KeyFilterOptions
): boolean {
  // Pass-through: modifier combos (Ctrl+A/C/V/X/Z, Cmd, Alt).
  if (e.ctrlKey || e.metaKey || e.altKey) return false;

  // Pass-through: control keys (key length > 1 = bukan single character).
  if (e.key.length > 1) return false;

  // Digits selalu allowed.
  if (e.key >= '0' && e.key <= '9') return false;

  const input = e.target as HTMLInputElement;
  const value = input.value ?? '';
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? value.length;

  // Comma: hanya jika allowComma=true.
  if (e.key === ',') {
    if (options.allowComma) return false;
    e.preventDefault();
    return true;
  }

  // Minus: hanya di posisi 0, hanya satu di field.
  if (e.key === '-') {
    // Harus di posisi 0 (awal teks).
    if (start !== 0) {
      e.preventDefault();
      return true;
    }
    // Tidak boleh ada minus lain di porsi value yang TIDAK di-select
    // (porsi setelah `end` — karena selection [start..end] akan di-replace).
    const valueAfterSelection = value.substring(end);
    if (valueAfterSelection.includes('-')) {
      e.preventDefault();
      return true;
    }
    return false;
  }

  // Karakter lain (titik, huruf, simbol) → cegah.
  e.preventDefault();
  return true;
}

/**
 * Sanitasi string clipboard: buang karakter invalid, dedupe minus/comma,
 * pastikan minus hanya di awal & komainput hanya satu.
 *
 * Dipakai oleh {@link handleNumberInputPaste}.
 */
export function sanitizePastedNumberText(
  text: string,
  options: KeyFilterOptions
): string {
  // Step 1: buang semua karakter selain digit, comma (jika allowed), minus.
  const validPattern = options.allowComma ? /[^0-9,\-]/g : /[^0-9\-]/g;
  let result = text.replace(validPattern, '');

  // Step 2: minus → hanya satu, di awal.
  const hasMinus = result.includes('-');
  result = result.replace(/-/g, '');
  if (hasMinus) result = '-' + result;

  // Step 3: comma → hanya satu (yang pertama dipertahankan, sisanya dibuang).
  if (options.allowComma) {
    const firstCommaIdx = result.indexOf(',');
    if (firstCommaIdx !== -1) {
      result =
        result.substring(0, firstCommaIdx + 1) +
        result.substring(firstCommaIdx + 1).replace(/,/g, '');
    }
  }

  return result;
}

/**
 * Handle paste event: sanitasi konten clipboard, gabung dengan value existing
 * dengan mempertimbangkan selection, lalu set ulang value via native setter.
 *
 * Catatan: setelah paste, **seluruh value** di-sanitasi ulang (bukan hanya
 * porsi yang di-paste) — supaya kombinasi value lama + paste tetap memenuhi
 * aturan (mis. tidak ada 2 minus, tidak ada 2 koma).
 */
export function handleNumberInputPaste(
  e: ClipboardEvent<HTMLInputElement>,
  options: KeyFilterOptions
): void {
  e.preventDefault();

  const input = e.target as HTMLInputElement;
  const pasted = e.clipboardData.getData('text');
  const value = input.value ?? '';
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? 0;

  // Gabung: value sebelum selection + paste + value setelah selection.
  const combined =
    value.substring(0, start) + pasted + value.substring(end);

  // Sanitasi seluruh hasil — ini menjamin aturan minus/comma uniqueness
  // berlaku terhadap kombinasi (bukan hanya potongan paste-nya saja).
  const sanitized = sanitizePastedNumberText(combined, options);

  setNativeInputValue(input, sanitized);

  // Cursor: posisi setelah karakter terakhir hasil sanitasi (tidak presisi
  // sempurna karena sanitasi bisa drop karakter — tapi cukup untuk UX).
  requestAnimationFrame(() => {
    input.setSelectionRange(sanitized.length, sanitized.length);
  });
}
